import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import CustomButton from "../CustomButton/CustomButton";
import {
  commonStyles,
  modalCommonStyles,
} from "../../Assests/styles/commonStyles";
import { styles } from "./CustomPromptStyles";

const CustomPrompt = ({
  open,
  fullWidth,
  maxWidth,
  showLoader,
  omitCloseButton,
  closeModalHandler,
  modalIcon,
  modalIconAlt,
  heading,
  descriptionLineOne,
  descriptionLineTwo,
  descriptionLineThree,
  buttonOneText,
  onButtonOneClick,
  buttonOneCustomStyles,
  buttonTwoText,
  onButtonTwoClick,
  buttonTwoCustomStyles,
  customStylesForLineTwo,
  customStylesForLineThree,
  dangerLineThree,
  isButtonOneDangerButton,
  customHeadingStyles,
  customDescriptionComponent,
}) => {
  useEffect(() => {
    window.addEventListener("keyup", handleKeyUp, false);
    return () => {
      window.removeEventListener("keyup", handleKeyUp, false);
    };
  }, []);

  const handleKeyUp = (e) => {
    const keys = {
      27: () => {
        e.preventDefault();
        if (omitCloseButton) {
          return;
        }
        closeModalHandler();
      },
    };
    if (keys[e.keyCode]) {
      keys[e.keyCode]();
    }
  };

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth ? maxWidth : "sm"}
      sx={modalCommonStyles.modal}
      open={open}
      aria-label={heading || descriptionLineOne || "Dialog"}
    >
      <Box sx={styles.container}>
        {showLoader ? (
          <Box
            sx={{
              ...modalCommonStyles.loaderContainer,
              ...styles.loaderStyles,
            }}
          >
            <CircularProgress sx={modalCommonStyles.loader} />
          </Box>
        ) : (
          <>
            {omitCloseButton ? null : (
              <Box sx={styles.buttonContainer}>
                <Button
                  onClick={closeModalHandler}
                  sx={styles.closeButton}
                  aria-label="Close dialog"
                >
                  <ClearIcon sx={styles.closeIcon} />
                </Button>
              </Box>
            )}
            <Box>
              <Box sx={styles.headingBox}>
                <Typography
                  sx={{ ...commonStyles.heading, ...customHeadingStyles }}
                >
                  {heading}
                </Typography>
              </Box>
              <Box sx={commonStyles.highlightBox}>
                <Box sx={{ ...styles.row, ...commonStyles.mobileModalRow }}>
                  <Box>
                    <Typography
                      component="img"
                      sx={styles.image}
                      src={modalIcon}
                      alt={modalIconAlt || "image"}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        ...styles.lineOne,
                        ...commonStyles.mobileModalText,
                        ...(descriptionLineTwo ? styles.marginBottom : {}),
                      }}
                    >
                      {descriptionLineOne}
                    </Typography>
                    {!!descriptionLineTwo && (
                      <Typography
                        sx={{
                          ...styles.descriptionOtherLines,
                          ...commonStyles.mobileModalText,
                          ...(descriptionLineThree ? styles.marginBottom : {}),
                          ...customStylesForLineTwo,
                        }}
                      >
                        {descriptionLineTwo}
                      </Typography>
                    )}
                    {!!descriptionLineThree && (
                      <Typography
                        sx={{
                          ...styles.descriptionOtherLines,
                          ...commonStyles.mobileModalText,
                          ...(dangerLineThree ? styles.alert : {}),
                          ...customStylesForLineThree,
                        }}
                      >
                        {descriptionLineThree}
                      </Typography>
                    )}
            
                    {!!customDescriptionComponent && (
                      <Box
                        sx={{
                          ...styles.descriptionOtherLines,
                          ...commonStyles.mobileModalText,
                          ...(dangerLineThree ? styles.alert : {}),
                          ...customStylesForLineThree,
                        }}
                      >
                        {customDescriptionComponent}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={commonStyles.buttonBox}>
                  <CustomButton
                    onClick={onButtonOneClick}
                    classes={{
                      ...commonStyles.commonButtonStyles,
                      ...buttonOneCustomStyles,
                    }}
                    dangerButton={isButtonOneDangerButton}
                  >
                    {buttonOneText}
                  </CustomButton>
                  {!!buttonTwoText && (
                    <CustomButton
                      onClick={onButtonTwoClick}
                      classes={{
                        ...commonStyles.commonButtonStyles,
                        ...commonStyles.buttonTwoStyles,
                        ...buttonTwoCustomStyles,
                      }}
                    >
                      {buttonTwoText}
                    </CustomButton>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

CustomPrompt.propTypes = {
  open: PropTypes.bool,
  fullWidth: PropTypes.bool,
  maxWidth: PropTypes.string,
  showLoader: PropTypes.bool,
  omitCloseButton: PropTypes.bool,
  closeModalHandler: PropTypes.func,
  modalIcon: PropTypes.string,
  modalIconAlt: PropTypes.string,
  heading: PropTypes.string,
  descriptionLineOne: PropTypes.string,
  descriptionLineTwo: PropTypes.string,
  descriptionLineThree: PropTypes.string,
  buttonOneText: PropTypes.string,
  onButtonOneClick: PropTypes.func,
  buttonOneCustomStyles: PropTypes.object,
  buttonTwoText: PropTypes.string,
  onButtonTwoClick: PropTypes.func,
  buttonTwoCustomStyles: PropTypes.object,
  customStylesForLineTwo: PropTypes.object,
  customStylesForLineThree: PropTypes.object,
  dangerLineThree: PropTypes.bool,
  isButtonOneDangerButton: PropTypes.bool,
  customHeadingStyles: PropTypes.object,
  customDescriptionComponent: PropTypes.node,
};

export default CustomPrompt;
